/* eslint-disable max-lines-per-function */
import { useEffect } from 'react';
import { useUnmount } from 'react-use';

import { ChronicDiseaseSimple } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { VisitFormController } from './visitForm.types';
import { registerVisitFormSynchronizationEvent, VisitDetailType } from './VisitFormSynchronization.event';
import {
   useVisitDiagnosisCreateAction,
   useVisitDiagnosisUpdateAdditionalInformationAction,
   useVisitFormSyncingState,
} from './VisitFormSynchronization.hooks';
import {
   createDiagnosesChangeHandler,
   createProceduresChangeHandler,
   createTextDetailChangeHandler,
   getFieldName,
} from './VisitFormSynchronization.utils';

type VisitFormSynchronizationProps = {
   readonly controller: VisitFormController;
   readonly chronicDiseases: ChronicDiseaseSimple[];
   readonly visitId: number;
};

export const VisitFormSynchronization = (props: VisitFormSynchronizationProps): null => {
   const [, setVisitFormSyncing] = useVisitFormSyncingState();
   const { executeAsync: createVisitDiagnosis } = useVisitDiagnosisCreateAction({
      visitId: props.visitId,
   });
   const { executeAsync: updateDiagnosisAdditionalInformation } = useVisitDiagnosisUpdateAdditionalInformationAction({
      visitId: props.visitId,
   });

   useEffect(() => {
      return registerVisitFormSynchronizationEvent(async (event) => {
         try {
            setVisitFormSyncing(true);
            const { diagnoses, procedures, interview, subjectiveAssessment, recommendations } = event.detail;
            const {
               diagnoses: { value: currentDiagnoses },
               procedures: { value: currentProcedures },
               interview: currentInterview,
               subjectiveAssessments: currentSubjectiveAssessment,
               recommendations: currentRecommendations,
            } = props.controller.watch();

            const textDetailChangeHandler = createTextDetailChangeHandler();
            const diagnosesChangeHandler = createDiagnosesChangeHandler({
               chronicDiseases: props.chronicDiseases,
               createVisitDiagnosis: createVisitDiagnosis,
               updateDiagnosisAdditionalInformation: updateDiagnosisAdditionalInformation,
            });
            const proceduresChangeHandler = createProceduresChangeHandler();

            props.controller.setValue('interview', textDetailChangeHandler(currentInterview, interview), {
               shouldValidate: true,
            });
            props.controller.setValue(
               'subjectiveAssessments',
               textDetailChangeHandler(currentSubjectiveAssessment, subjectiveAssessment)
            );
            props.controller.setValue(
               'recommendations',
               textDetailChangeHandler(currentRecommendations, recommendations)
            );
            props.controller.setValue(
               'diagnoses.value',
               diagnoses ? await diagnosesChangeHandler(currentDiagnoses, { ...diagnoses }) : currentDiagnoses
            );
            props.controller.setValue(
               'procedures.value',
               procedures ? proceduresChangeHandler(procedures) : currentProcedures
            );

            props.controller.markDirty();

            const detailKeys = Object.keys(event.detail).filter((key) => key !== '_type') as VisitDetailType[];

            if (detailKeys.length === 1) {
               props.controller.setFocus(getFieldName(detailKeys[0]));
            }
         } finally {
            setVisitFormSyncing(false);
         }
      });
   }, [
      createVisitDiagnosis,
      props.chronicDiseases,
      props.controller,
      setVisitFormSyncing,
      updateDiagnosisAdditionalInformation,
   ]);

   useUnmount(() => {
      setVisitFormSyncing(false);
   });

   return null;
};
